import React, { useEffect, useState } from "react";
import { message } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { NextButton, ButtonComponent } from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import EntitySection from "./EntitySection";
import ReadinessSummary from "./ReadinessSummary";

// Existing Create/Update modals (reused as-is)
import CreateCompany from "@mods/organizationManagement/companies/components/CreateCompany";
import UpdateCompany from "@mods/organizationManagement/companies/components/UpdateCompany";
import CreateLocation from "@mods/organizationManagement/locations/components/CreateLocation";
import UpdateLocation from "@mods/organizationManagement/locations/components/UpdateLocation";
import CreateArea from "@mods/organizationManagement/areas/components/CreateArea";
import UpdateArea from "@mods/organizationManagement/areas/components/UpdateArea";
import CreateBranch from "@mods/organizationManagement/branches/components/CreateBranch";
import UpdateBranch from "@mods/organizationManagement/branches/components/UpdateBranch";
import CreateDepartment from "@mods/organizationManagement/departments/components/CreateDepartment";
import UpdateDepartment from "@mods/organizationManagement/departments/components/UpdateDepartment";
import CreateGrade from "@mods/organizationManagement/grades/components/CreateGrade";
import UpdateGrade from "@mods/organizationManagement/grades/components/UpdateGrade";
import CreateDesignation from "@mods/organizationManagement/designations/components/CreateDesignation";
import UpdateDesignation from "@mods/organizationManagement/designations/components/UpdateDesignation";
import CreateJobTitle from "@mods/organizationManagement/jobTitles/components/CreateJobTitle";
import UpdateJobTitle from "@mods/organizationManagement/jobTitles/components/UpdateJobTitle";

import {
  getCompanies, getAllTimeZonesReq,
  getAllLocationsReq, updateLocationReq,
  getAllAreasReq, updateAreaReq,
  getAllBranches, updateBranch,
  getAllDepartments, updateDepartmentReq,
  getAllGrades, updateGradeReq,
  getAllDesignations, updateDesignation,
  getAllJobTitlesReq, updateJobTitleReq,
} from "../requests";

// The 7 list-based entities (company is single-record, handled separately).
const SECTIONS = [
  { key: "locations", label: "Locations", listReq: getAllLocationsReq, listKey: "locations", updateReq: updateLocationReq, Create: CreateLocation, Update: UpdateLocation },
  { key: "areas", label: "Areas", listReq: getAllAreasReq, listKey: "areas", updateReq: updateAreaReq, Create: CreateArea, Update: UpdateArea },
  { key: "branches", label: "Branches", listReq: getAllBranches, listKey: "branchs", updateReq: updateBranch, Create: CreateBranch, Update: UpdateBranch },
  { key: "departments", label: "Departments", listReq: getAllDepartments, listKey: "departments", updateReq: updateDepartmentReq, Create: CreateDepartment, Update: UpdateDepartment, getExtraProps: (items) => ({ departments: items }) },
  { key: "grades", label: "Grades", listReq: getAllGrades, listKey: "grades", updateReq: updateGradeReq, Create: CreateGrade, Update: UpdateGrade },
  { key: "designations", label: "Designations", listReq: getAllDesignations, listKey: "designations", updateReq: updateDesignation, Create: CreateDesignation, Update: UpdateDesignation },
  { key: "jobTitles", label: "Job Titles", listReq: getAllJobTitlesReq, listKey: "job_titles", updateReq: updateJobTitleReq, Create: CreateJobTitle, Update: UpdateJobTitle },
];

// ---- Company (single record) ---------------------------------------------
const CompanySection = ({ onReady }) => {
  const [company, setCompany] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [child, setChild] = useState(null);

  useEffect(() => {
    makeRequestStateless(getAllTimeZonesReq, false, (d) => setTimezones(d || []), null);
    load();
    // eslint-disable-next-line
  }, []);

  const load = () => {
    makeRequestStateless(() => getCompanies(""), null, (data) => {
      const rec = data?.companies?.data?.[0] || null;
      setCompany(rec);
      if (rec?.id) localStorage.setItem("company_id", rec.id);
      if (onReady) onReady(rec);
    }, () => {});
  };

  // Save passes the refreshed company array; cancel/close passes `false`.
  // Only apply on a real save — closing the modal must not call the API.
  const onDone = (data) => {
    setChild(null);
    if (!data) return;
    const rec = Array.isArray(data) ? data[0] : data;
    if (rec && rec.id) {
      setCompany(rec);
      localStorage.setItem("company_id", rec.id);
      if (onReady) onReady(rec);
    }
  };

  const openCreate = () => setChild(<CreateCompany onCreated={onDone} timezones={timezones} currencies={[]} />);
  const openEdit = () => setChild(<UpdateCompany record={company} disabled={false} onCreated={onDone} timezones={timezones} currencies={[]} />);

  return (
    <div className="onb-panel" style={{ marginBottom: 16 }}>
      <div className="onb-panel-h">
        <span>Company</span>
        <span className="c">single record</span>
      </div>
      <div style={{ padding: 14 }}>
        {company ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#1c1a17" }}>{company.name}</div>
              <div style={{ fontSize: 12, color: "#756e62" }}>
                {company.short_name || company.code ? `Code prefix: ${company.short_name || company.code}` : "Configured"}
              </div>
            </div>
            <ButtonComponent icon={<EditOutlined />} text="Edit company" onClick={openEdit} />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, fontSize: 13, color: "#756e62" }}>
              No company yet. Create one to continue — everything else attaches to it.
            </div>
            <ButtonComponent type="primary" icon={<PlusOutlined />} text="Create company" onClick={openCreate} />
          </div>
        )}
      </div>
      {child}
    </div>
  );
};

// ---- Step 1 hub -----------------------------------------------------------
const Step1Org = (props) => {
  const { patch, onNext, readiness, readinessLoading, refreshReadiness, statuses } = props;
  const [companyReady, setCompanyReady] = useState(false);
  const [counts, setCounts] = useState({});

  const onCompany = (rec) => {
    setCompanyReady(!!rec?.id);
    if (rec) patch({ company: { id: rec.id, name: rec.name, short_name: rec.short_name || rec.code } });
    refreshReadiness?.();
  };

  const setSectionCount = (key, n) => setCounts((c) => ({ ...c, [key]: n }));

  const proceed = () => {
    const liveCompanyReady = companyReady || readiness.company.exists;
    const effectiveCounts = { ...(readiness.org.counts || {}), ...counts };
    const missing = [
      !liveCompanyReady ? "Company" : null,
      (effectiveCounts.locations || 0) < 1 ? "Locations" : null,
      (effectiveCounts.areas || 0) < 1 ? "Areas" : null,
      (effectiveCounts.branches || 0) < 1 ? "Branches" : null,
      (effectiveCounts.departments || 0) < 1 ? "Departments" : null,
      (effectiveCounts.grades || 0) < 1 ? "Grades" : null,
      (effectiveCounts.designations || 0) < 1 ? "Designations" : null,
      (effectiveCounts.jobTitles || 0) < 1 ? "Job titles" : null,
    ].filter(Boolean);
    if (!liveCompanyReady) {
      message.info("Create your company first.");
      return;
    }
    if (missing.length) {
      message.info(`Complete organisation setup first: ${missing.join(", ")}.`);
      return;
    }
    patch({ org: { counts: effectiveCounts } });
    refreshReadiness?.();
    onNext();
  };

  const orgCounts = readiness.org.counts || {};

  return (
    <>
      <div className="onb-card-title">Set up your organisation</div>
      <div className="onb-card-sub">
        Review the company, locations, branches and employee master data that HR uses everywhere else.
        Add missing items here; disabling existing records asks for confirmation.
      </div>

      <ReadinessSummary
        status={statuses.company}
        loading={readinessLoading}
        onRefresh={refreshReadiness}
        items={[
          { label: "Company", value: readiness.company.exists ? "Found" : "Missing", help: readiness.company.record?.name },
          { label: "Locations", value: orgCounts.locations || 0 },
          { label: "Areas", value: orgCounts.areas || 0 },
          { label: "Branches", value: orgCounts.branches || 0 },
          { label: "Departments", value: orgCounts.departments || 0 },
          { label: "Grades", value: orgCounts.grades || 0 },
          { label: "Designations", value: orgCounts.designations || 0 },
          { label: "Job titles", value: orgCounts.jobTitles || 0 },
        ]}
      />

      {readiness.org.missingRequired?.length ? (
        <div className="onb-empty-guidance">
          Missing required setup: {readiness.org.missingRequired.join(", ")}. Add these before moving ahead.
        </div>
      ) : null}

      <CompanySection onReady={onCompany} />

      {SECTIONS.map((s) => (
        <EntitySection
          key={s.key}
          label={s.label}
          listReq={s.listReq}
          listKey={s.listKey}
          updateReq={s.updateReq}
          CreateComp={s.Create}
          UpdateComp={s.Update}
          getExtraProps={s.getExtraProps}
          onItemsChange={(arr) => setSectionCount(s.key, arr.length)}
        />
      ))}

      <div className="onb-foot">
        <span className="spacer" />
        <NextButton onClick={proceed} disabled={!(companyReady || readiness.company.exists)} text="Continue" />
      </div>
    </>
  );
};

export default Step1Org;
